import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };

type State = { error: Error | null };

/**
 * Surfaces init/render failures (e.g. bad Clerk key) instead of a blank **`#root`**.
 */
export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[customer-web]", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      const { message, stack } = this.state.error;
      return (
        <div
          className="min-h-screen bg-surface px-6 py-10 text-left font-sans text-dark"
          role="alert"
        >
          <h1 className="text-xl font-semibold text-red">Customer web failed to render</h1>
          <p className="mt-4 max-w-2xl text-sm text-gray">
            Open the browser devtools console (⌥⌘J / F12) for the full error. Common causes: invalid{" "}
            <code className="rounded border border-border bg-white px-1 font-mono text-sm">VITE_CLERK_PUBLISHABLE_KEY</code> (remove or
            replace placeholder <code className="rounded border border-border bg-white px-1 font-mono text-sm">pk_test_...</code>), or a
            router/runtime exception.
          </p>
          <pre className="mt-6 max-w-3xl overflow-x-auto rounded-lg border border-border bg-white p-4 text-sm text-red">
            {message}
          </pre>
          {import.meta.env.DEV && stack ? (
            <pre className="mt-4 max-w-3xl overflow-x-auto text-xs text-gray">{stack}</pre>
          ) : null}
        </div>
      );
    }
    return this.props.children;
  }
}
